import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { useLang } from "@/context/LanguageContext";
import Reveal from "@/components/Reveal";

const images = [
  "/images/lieux/prieure-11.webp",
  "/images/lieux/interieur-81.webp",
  "/images/lieux/prieure-07.webp",
];

const Chapter = ({ chapter, image, index }) => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], [50, -50]);
  const flip = index % 2 === 1;

  return (
    <article
      ref={ref}
      data-testid={`manifesto-chapter-${chapter.num}`}
      className="grid grid-cols-1 items-center gap-10 lg:grid-cols-12 lg:gap-16"
    >
      <div className={`lg:col-span-5 ${flip ? "lg:order-2" : ""}`}>
        <Reveal>
          <div className="gold-frame relative aspect-[4/5] overflow-hidden">
            <motion.img
              style={{ y }}
              src={image}
              alt={chapter.title}
              className="h-[115%] w-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-ink/60 via-transparent to-transparent" />
          </div>
        </Reveal>
      </div>
      <div className={`lg:col-span-7 ${flip ? "lg:order-1 lg:text-right" : ""}`}>
        <Reveal delay={0.1}>
          <span className="font-display text-6xl text-transparent sm:text-7xl" style={{ WebkitTextStroke: "1px rgba(212,175,55,0.55)" }}>
            {chapter.num}
          </span>
        </Reveal>
        <Reveal delay={0.18}>
          <h3 className="mt-4 font-serif text-4xl font-light text-parchment sm:text-5xl lg:text-6xl">
            {chapter.title}
          </h3>
        </Reveal>
        <Reveal delay={0.26}>
          <p className={`mt-6 max-w-xl text-base font-light leading-relaxed text-sand sm:text-lg ${flip ? "lg:ml-auto" : ""}`}>
            {chapter.text}
          </p>
        </Reveal>
        <Reveal delay={0.34}>
          <div className={`mt-8 h-px w-24 bg-gold/50 ${flip ? "lg:ml-auto" : ""}`} />
        </Reveal>
      </div>
    </article>
  );
};

const Manifesto = () => {
  const { t } = useLang();

  return (
    <section id="manifeste" data-testid="manifesto-section" className="relative mx-auto max-w-[1600px] px-4 py-24 sm:px-8 lg:px-16 lg:py-36">
      <Reveal>
        <p className="font-mono text-[11px] uppercase tracking-[0.35em] text-gold">{t.manifesto.kicker}</p>
      </Reveal>
      <Reveal delay={0.1}>
        <h2 className="mt-4 max-w-3xl font-serif text-3xl font-light leading-tight text-parchment sm:text-4xl lg:text-5xl">
          {t.manifesto.title}
        </h2>
      </Reveal>

      <div className="mt-20 space-y-28 lg:mt-28 lg:space-y-40">
        {t.manifesto.chapters.map((chapter, i) => (
          <Chapter key={chapter.num} chapter={chapter} image={images[i]} index={i} />
        ))}
      </div>
    </section>
  );
};

export default Manifesto;
